import type { QuestionKind } from "@talenthop/shared";

/**
 * Candidate · how the interview's position is DESCRIBED. Pure, so the one number the candidate
 * watches for twenty minutes is decided somewhere it can be asserted.
 *
 * The engine is allowed to extend an interview: after an answer worth probing it mints a bounded
 * adaptive follow-up, so a 14-question plan legitimately asks 18. What was wrong was the telling.
 * The screen rendered `answered/total` where `total` is a LIVE ROW COUNT, so the denominator grew
 * under the candidate — "3 of 14" became "4 of 15" became "5 of 16" — and the submit button
 * promised "Finish & submit interview" on a question the runtime then followed up on.
 *
 * The rule here: THE BAR AND THE COUNTER TRACK THE PLAN, follow-ups are disclosed beside them as
 * what they are, and nothing claims to be the last question unless the server says the total can
 * no longer grow.
 */

/** The `progress` object from GET/POST …/interview/*. Every field past `total` is optional so a
 *  browser holding an older bundle against a newer API — or the reverse — still renders. */
export interface ProgressPayload {
  answered: number;
  total: number;
  planned?: number;
  followUps?: number;
  answeredBase?: number;
  mayExtend?: boolean;
}

export interface ProgressView {
  /** 0–100 for the bar, off the PLAN, so a follow-up landing cannot make it walk backwards. */
  percent: number;
  /** The plan position, e.g. 4 of 14. `position` never advances on a follow-up. */
  position: number;
  planned: number;
  /** "+2 follow-up questions", or null when the engine has not probed anything yet. */
  followUpNote: string | null;
  /** The question on screen is a live follow-up, not one of the planned questions. */
  onFollowUp: boolean;
  /** Whether the UI may say "this is the last one". False whenever the total can still grow. */
  canPromiseLast: boolean;
  /**
   * What the submit button is allowed to claim:
   *   "next"    — a further question definitely exists (it is already on the server)
   *   "last"    — this is the end and the server cannot add to it
   *   "unknown" — the last question we know of, but a follow-up may still be minted after it.
   *               Saying "Finish & submit interview" here is the promise that got broken.
   */
  submitIntent: "next" | "last" | "unknown";
}

/**
 * @param currentKind the kind of the question on screen, or null when none is showing.
 *
 * A follow-up is identified as `adaptive_challenge` AND the server reporting at least one
 * follow-up. That second half matters: the vertical-fallback path serves its seq-3 challenge as
 * one of its six PLANNED questions (followUps stays 0 there), and calling that "a follow-up" would
 * drop the counter by one halfway through every fallback interview.
 */
export function progressView(p: ProgressPayload, currentKind: QuestionKind | null): ProgressView {
  const planned = Math.max(0, p.planned ?? p.total);
  const followUps = Math.max(0, p.followUps ?? 0);
  const answeredBase = Math.min(Math.max(0, p.answeredBase ?? p.answered), planned);
  const onFollowUp = currentKind === "adaptive_challenge" && followUps > 0;
  const moreQueued = p.answered + 1 < p.total;
  const canPromiseLast = !(p.mayExtend ?? false) && !moreQueued;

  return {
    percent: planned > 0 ? Math.round((answeredBase / planned) * 100) : 0,
    // On a follow-up the plan position stays on the question being probed — the candidate is not
    // one question further along, they are still on this one.
    position: onFollowUp ? Math.max(answeredBase, 1) : Math.min(answeredBase + 1, Math.max(planned, 1)),
    planned,
    followUpNote:
      followUps > 0 ? `+${followUps} follow-up question${followUps === 1 ? "" : "s"}` : null,
    onFollowUp,
    canPromiseLast,
    submitIntent: moreQueued ? "next" : canPromiseLast ? "last" : "unknown",
  };
}
